import {getIconByType, getIconColorByType, openLibraryFile, libraryTypeLabels} from "../utils/library-utils.js";
import {useCallback, useEffect, useMemo, useRef} from "react";
import {AgGridReact} from "ag-grid-react";
import AgGridUtils from "../../../../utils/aggrid-utils.js";
import DropdownButton from "../../../../components/button/dropdown-button.jsx";
import {FaEllipsisVertical} from "react-icons/fa6";
import ProjectUtils from "../../../../utils/project-utils.js";
import LibraryDropzone from "./library-dropzone.jsx";
import {navigateHook} from "../../../../hooks/use-navigate.js";

function ActionsCell({ data, buttonOptions }) {
    if (!data) return null;

    const optionsWithItem = buttonOptions.map((option) => ({
        ...option,
        disabled: typeof option.disabled === "function" ? option.disabled(data) : option.disabled,
        successText: typeof option.successText === "function" ? option.successText(data) : option.successText,
        onClick: () => option.onClick(data),
    }));

    return (
        <div
            data-aggrid-actions
            className="flex justify-center"
        >
            <DropdownButton
                buttonContent={<FaEllipsisVertical />}
                buttonClassName="
                    flex h-9 w-9 shrink-0 items-center justify-center
                    rounded-full bg-transparent cursor-pointer
                    hover:bg-[#d1d5db] transition-colors duration-200 ease-in-out
                    hover:opacity-70
                "
                menuClassName="!z-[1000]"
                items={optionsWithItem}
            />
        </div>
    );
}

export default function LibraryList({ items, isSearching = false, isLoading = false, isFetching = false, hasPermission, buttonOptions = [], onOpenFolder, onDropFile, selectedItemIds = [], onSelectItem }) {
    const navigate = navigateHook();
    const gridRef = useRef(null);
    const buttonOptionsRef = useRef(buttonOptions);

    buttonOptionsRef.current = buttonOptions;

    const handleOpenItem = useCallback(async (item) => {
        if (!item) return;

        if (item.type === "FOLDER") {
            onOpenFolder(item);
            return;
        }

        if (item.type === "FILE") {
            await openLibraryFile(item);
            return;
        }

        if (item.type === "DOCUMENT") {
            navigate(`/library/document/form/${item.id}`, true);
        }
    }, [onOpenFolder]);

    const gridOptions = useMemo(() => ({ localeText: AgGridUtils.localization() }), []);

    const gridTheme = useMemo(() => AgGridUtils.generalTheme({ rowHeight: 40 }), []);

    const rowSelection = useMemo(() => ({
        mode: "multiRow",
        enableClickSelection: false,
        checkboxes: false,
        headerCheckbox: false,
    }), []);

    const columnDefs = useMemo(() => {
        return [
            {
                headerName: "Nome",
                field: "name",
                flex: 2,
                cellRenderer: ({ data }) => {
                    if (!data) return null;
                    const Icon = getIconByType(data);
                    const iconColorClassName = getIconColorByType(data);

                    return (
                        <div className="flex items-center gap-2">
                            <Icon size={20} className={`shrink-0 ${iconColorClassName}`}/>
                            <span>{data.name}</span>
                        </div>
                    )
                },
                comparator: AgGridUtils.lowerUpCaseComparator,
            },
            {
                headerName: "Tipo de arquivo",
                field: "type",
                maxWidth: 120,
                minWidth: 120,
                valueFormatter: ({ value }) => libraryTypeLabels[value],
            },
            {
                headerName: "Criado por",
                field: "userCreated",
                flex: 1,
            },
            {
                headerName: "Última atualização",
                field: "updatedAt",
                flex: 1.5,
                valueGetter: (p) => {
                    const updated = p.data?.updatedAt ? ProjectUtils.formatDate(p.data?.updatedAt) : "Não informado";
                    const name = p.data?.userUpdated || "Não informado";
                    return `${updated} - ${name}`
                }
            },
            {
                headerName: "",
                field: "actions",
                colId: "actions",
                filter: false,
                sortable: false,
                minWidth: 70,
                maxWidth: 70,
                cellRenderer: ({ data }) => (
                    <ActionsCell
                        data={data}
                        buttonOptions={buttonOptionsRef.current}
                    />
                )
            }
        ];
    }, []);

    const handleRowClick = useCallback((event) => {
        const clickedInsideAction = event.event.target.closest("[data-aggrid-actions]");

        if (clickedInsideAction) return;

        onSelectItem?.(event.data, event.event);
    }, [onSelectItem]);

    useEffect(() => {
        const api = gridRef.current?.api;

        if (!api) return;

        api.forEachNode((node) => {
            const isSelected = selectedItemIds.includes(node.data?.id);
            node.setSelected(isSelected);
        });
    }, [selectedItemIds, items]);

    useEffect(() => {
        gridRef.current?.api?.refreshCells({
            columns: ["actions"],
            force: true,
        });
    }, [buttonOptions]);

    return (
        <div
            className="aggrid-row-pointer h-[calc(100vh-196px)] w-full"
            onDragOver={(event) => {
                event.preventDefault();
                event.stopPropagation();
            }}
            onDrop={(event) => {
                event.preventDefault();
                event.stopPropagation();

                if (!onDropFile) return;

                onDropFile(event.dataTransfer.files);
            }}
        >
            {isLoading ? (
                <div className="flex h-full w-full items-center justify-center">
                    Carregando...
                </div>
            ) : items.length > 0 ? (
                <div
                    className={`
                        h-full w-full transition-opacity duration-150
                        ${isFetching ? "opacity-70" : "opacity-100"}
                    `}>
                    <AgGridReact
                        ref={gridRef}
                        rowData={items}
                        columnDefs={columnDefs}
                        gridOptions={gridOptions}
                        theme={gridTheme}
                        getRowId={({ data }) => String(data.id)}
                        suppressCellFocus={true}
                        rowSelection={rowSelection}
                        onRowClicked={handleRowClick}
                        onRowDoubleClicked={(event) => {
                            const clickedInsideActions = event.event.target.closest("[data-aggrid-actions]");

                            if (clickedInsideActions) return;

                            handleOpenItem(event.data);
                        }}
                        onCellKeyDown={(event) => {
                            if (event.column?.getColId() === "actions") {
                                return;
                            }

                            if (event.event.key === "Enter" || event.event.key === " ") {
                                event.event.preventDefault();
                                handleOpenItem(event.data);
                            }
                        }}
                        animateRows={false}
                    />
                </div>
            ) : (
                <LibraryDropzone
                    isSearching={isSearching}
                    onSelectFile={onDropFile}
                    hasPermission={hasPermission}
                />
            )}
        </div>
    )
}